import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ClinicorpMenuService } from './clinicorp-menu.service';
import { AlertComponent } from '../../../alert/alert.component';
import { Subject, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

const numberPattern = /^[0-9]+$/;

@Component({
  selector: 'app-clinicorp-menu',
  standalone: true,
  imports: [CommonModule, MatIconModule, ReactiveFormsModule, AlertComponent],
  templateUrl: './clinicorp-menu.component.html',
  styleUrls: ['./clinicorp-menu.component.scss'],
  providers: [ClinicorpMenuService],
})
export class ClinicorpMenuComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  public clinicas: Array<any> = [];
  public exibirModalCadastro = false;
  public exibirModalVariaveis = false;
  public cadastroClinicaForm!: FormGroup;
  public edicaoClinicaForm!: FormGroup;
  public loadingSubmit = false;
  public exibirModalDetalhe = false;
  public clinicaSelecionada: any;
  public loadingBusinessIdCadastro = false;
  public loadingBusinessIdEdicao = false;
  public variaveisDisponiveis = [
    { chave: '{{nome}}', descricao: 'Nome do paciente' },
    { chave: '{{data}}', descricao: 'Data do atendimento' },
    { chave: '{{horario}}', descricao: 'Horário do atendimento' },
  ];
  @ViewChild(AlertComponent) alertComponent!: AlertComponent;

  constructor(private clinicorpService: ClinicorpMenuService, private fb: FormBuilder) {}

  ngOnInit(): void {
    this.cadastroClinicaForm = this.fb.group({
      subscribeId: ['', Validators.required],
      businessId: ['', [Validators.required, Validators.pattern(numberPattern)]],
      nomeClinica: ['', Validators.required],
      somaDia: ['', Validators.required],
      mensagemAniversario: ['', Validators.required],
      mensagemLembrete: ['', Validators.required],
      mensagemConfirmacao: ['', Validators.required],
      timeExecution: ['', [Validators.required, Validators.pattern(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/)]],
      host: ['', Validators.required],
      queueId: ['', [Validators.required, Validators.pattern(/^[0-9]+$/)]],
      apiKey: ['', Validators.required],
      token: ['', Validators.required],
      ativo: [true, Validators.required],
    });

    this.edicaoClinicaForm = this.fb.group({
      id_clinic: [''],
      subscribeId: ['', Validators.required],
      businessId: ['', [Validators.required, Validators.pattern(numberPattern)]],
      nomeClinica: ['', Validators.required],
      somaDia: ['', Validators.required],
      mensagemAniversario: ['', Validators.required],
      mensagemLembrete: ['', Validators.required],
      mensagemConfirmacao: ['', Validators.required],
      timeExecution: ['', [Validators.required, Validators.pattern(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/)]],
      host: ['', Validators.required],
      queueId: ['', [Validators.required, Validators.pattern(/^[0-9]+$/)]],
      apiKey: ['', Validators.required],
      token: [''],
      ativo: [true, Validators.required],
    });

    this.listarClinicas();
    this.setupSubscriberIdListener(this.cadastroClinicaForm, 'cadastro');
    this.setupSubscriberIdListener(this.edicaoClinicaForm, 'edicao');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public listarClinicas(): void {
    this.clinicorpService.listarClinicas().subscribe({
      next: (clinicas: any) => this.clinicas = this.normalizarClinicas(clinicas),
      error: (err) => {
        console.error('Erro ao listar clínicas Clinicorp', err);
        this.clinicas = [];
      }
    });
  }

  public handleCadastrarClinica(): void {
    this.exibirModalCadastro = true;
  }

  public fecharModalCadastro(): void {
    this.exibirModalCadastro = false;
    this.cadastroClinicaForm.reset({ ativo: true });
  }

  public abrirModalVariaveis(event?: Event): void {
    event?.stopPropagation();
    this.exibirModalVariaveis = true;
  }

  public fecharModalVariaveis(): void {
    this.exibirModalVariaveis = false;
  }

  public copiarVariavel(valor: string, event?: Event): void {
    event?.stopPropagation();
    const copyPromise = navigator?.clipboard?.writeText
      ? navigator.clipboard.writeText(valor)
      : new Promise<void>((resolve, reject) => {
          // Fallback simples para navegadores que não suportam clipboard API
          const textarea = document.createElement('textarea');
          textarea.value = valor;
          document.body.appendChild(textarea);
          textarea.select();
          try {
            document.execCommand('copy');
            resolve();
          } catch (err) {
            reject(err);
          } finally {
            document.body.removeChild(textarea);
          }
        });

    copyPromise
      .then(() => this.alertComponent?.showAlert?.(`Variável ${valor} copiada`, 'success'))
      .catch((err) => {
        console.error('Erro ao copiar variável', err);
        this.alertComponent?.showAlert?.('Não foi possível copiar a variável', 'error');
      });
  }

  public submitCadastroClinica(): void {
    const formattedTime = this.normalizarHorario(this.cadastroClinicaForm.value.timeExecution);
    if (!formattedTime) {
      this.cadastroClinicaForm.get('timeExecution')?.setErrors({ invalidTime: true });
    } else {
      this.cadastroClinicaForm.patchValue({ timeExecution: formattedTime }, { emitEvent: false });
    }

    if (this.cadastroClinicaForm.invalid) {
      this.cadastroClinicaForm.markAllAsTouched();
      return;
    }

    const businessId = String(this.cadastroClinicaForm.value.businessId ?? '').replace(/\D/g, '');
    const queueId = String(this.cadastroClinicaForm.value.queueId ?? '').replace(/\D/g, '');
    const payload = {
      ...this.cadastroClinicaForm.value,
      timeExecution: formattedTime,
      businessId,
      queueId,
    };

    this.loadingSubmit = true;
    this.clinicorpService.cadastrarClinica(payload).subscribe({
      next: () => {
        this.loadingSubmit = false;
        this.fecharModalCadastro();
        this.listarClinicas();
      },
      error: (err) => {
        console.error('Erro ao cadastrar clínica Clinicorp', err);
        this.loadingSubmit = false;
      }
    });
  }

  public abrirDetalhesClinica(clinica: any): void {
    this.clinicaSelecionada = clinica;
    const horario = this.normalizarHorario(clinica?.timeExecution || '');
    this.edicaoClinicaForm.reset({
      id_clinic: clinica.id_clinic ?? '',
      subscribeId: clinica.subscribeId ?? '',
      businessId: clinica.businessId ?? '',
      nomeClinica: clinica.nomeClinica ?? '',
      somaDia: clinica.somaDia ?? '',
      mensagemAniversario: clinica.mensagemAniversario ?? '',
      mensagemLembrete: clinica.mensagemLembrete ?? '',
      mensagemConfirmacao: clinica.mensagemConfirmacao ?? '',
      timeExecution: horario || clinica.timeExecution || '',
      host: clinica.host ?? '',
      queueId: clinica.queueId ?? '',
      apiKey: clinica.apiKey ?? '',
      token: clinica.token ?? '',
      ativo: clinica.ativo ?? true,
    }, { emitEvent: false });
    this.exibirModalDetalhe = true;
  }

  public fecharModalDetalhe(): void {
    this.exibirModalDetalhe = false;
    this.clinicaSelecionada = null;
  }

  public submitAtualizarClinica(): void {
    console.log("Atualizando a clinica...");
    const formattedTime = this.normalizarHorario(this.edicaoClinicaForm.value.timeExecution);
    if (!formattedTime) {
      console.log("Flag 1");
      this.edicaoClinicaForm.get('timeExecution')?.setErrors({ invalidTime: true });
    } else {
      console.log("Flag 2");
      this.edicaoClinicaForm.patchValue({ timeExecution: formattedTime }, { emitEvent: false });

      console.log(this.edicaoClinicaForm.value);
    }

    if (this.edicaoClinicaForm.invalid) {
      console.log("Flag 3");
      this.edicaoClinicaForm.markAllAsTouched();
      return;
    }

    const { token, ...restValues } = this.edicaoClinicaForm.value;
    const businessId = String(restValues.businessId ?? '').replace(/\D/g, '');
    const queueId = String(restValues.queueId ?? '').replace(/\D/g, '');
    const payload = {
      ...restValues,
      ...(token ? { token } : {}),
      timeExecution: formattedTime,
      businessId,
      queueId,
    };

    this.loadingSubmit = true;
    this.clinicorpService.atualizarClinica(payload).subscribe({
      next: () => {
        this.loadingSubmit = false;
        this.fecharModalDetalhe();
        this.listarClinicas();
      },
      error: (err) => {
        console.error('Erro ao atualizar clínica Clinicorp', err);
        this.loadingSubmit = false;
      }
    });
  }

  public trackByNome(index: number, clinica: { nomeClinica: string }): string {
    return clinica.nomeClinica;
  }

  public onTimeExecutionChange(event: Event, form: FormGroup): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.normalizarHorario(input.value);
    if (formatted) {
      form.patchValue({ timeExecution: formatted }, { emitEvent: false });
    }
  }

  private setupSubscriberIdListener(form: FormGroup, context: 'cadastro' | 'edicao'): void {
    const subscribeControl = form.get('subscribeId');
    const tokenControl = form.get('token');
    if (!subscribeControl || !tokenControl) return;

    combineLatest([
      subscribeControl.valueChanges.pipe(startWith(subscribeControl.value)),
      tokenControl.valueChanges.pipe(startWith(tokenControl.value)),
    ])
      .pipe(
        debounceTime(600),
        map(([subscribeId, token]) => ({
          subscribeId: String(subscribeId ?? '').trim(),
          token: String(token ?? '').trim(),
        })),
        distinctUntilChanged(
          (prev, curr) =>
            prev.subscribeId === curr.subscribeId && prev.token === curr.token
        ),
        filter(({ subscribeId, token }) => {
          if (!subscribeId || !token) {
            form.patchValue({ businessId: '' }, { emitEvent: false });
            return false;
          }
          return true;
        }),
        switchMap(({ subscribeId, token }) => {
          this.setBusinessIdLoading(context, form, true);
          return this.clinicorpService.buscarBusinessPorSubscriber(subscribeId, token).pipe(
            catchError((error) => {
              console.error('Erro ao buscar Business ID', error);
              this.alertComponent?.showAlert?.('Erro ao buscar Business ID.', 'error');
              return of([]);
            }),
            finalize(() => this.setBusinessIdLoading(context, form, false))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        const businessId = this.extrairBusinessId(response);
        if (businessId) {
          form.patchValue({ businessId }, { emitEvent: false });
        } else {
          this.alertComponent?.showAlert?.('Business ID não encontrado para este Subscribe ID.', 'error');
        }
      });
  }

  private extrairBusinessId(response: any): string | null {
    const lista = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.items)
          ? response.items
          : [];
    const id = lista?.[0]?.id;
    return id ? String(id) : null;
  }

  private setBusinessIdLoading(context: 'cadastro' | 'edicao', form: FormGroup, loading: boolean): void {
    if (context === 'cadastro') {
      this.loadingBusinessIdCadastro = loading;
    } else {
      this.loadingBusinessIdEdicao = loading;
    }

    if (loading) {
      form.disable({ emitEvent: false });
    } else {
      form.enable({ emitEvent: false });
    }
  }

  private normalizarClinicas(response: any): Array<any> {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.clinicas)) return response.clinicas;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.items)) return response.items;
    if (response && typeof response === 'object') return Object.values(response);
    return [];
  }

  private normalizarHorario(value: string): string {
    if (!value) return '';
    // Aceita "12:30", "12:30:00" e variações com milissegundos/fuso, convertendo para HH:mm
    const match = value.match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/);
    if (!match) return '';
    let hours = parseInt(match[1], 10);
    let minutes = parseInt(match[2], 10);
    if (isNaN(hours) || isNaN(minutes) || hours > 23 || minutes > 59) return '';
    const h = hours.toString().padStart(2, '0');
    const m = minutes.toString().padStart(2, '0');
    return `${h}:${m}`;
  }
}
